import { useState, useEffect, useCallback } from "react";
import { Search, Plus, Car, Map } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { ConsortiumFormModal, DeleteConsortiumModal } from "../components/consortiums/ConsortiumModals";
import { ConsortiumsTable, ConsortiumsMobile } from "../components/consortiums/ConsortiumsTable";
import QuotaMap from "../components/consortiums/QuotaMap";

var STATUS_LABELS = { ativo: "Ativo", cancelado: "Cancelado", contemplado: "Contemplado", em_atraso: "Em Atraso" };
var STATUS_DOT = { ativo: "bg-emerald-400", cancelado: "bg-red-400", contemplado: "bg-blue-400", em_atraso: "bg-amber-400" };

export default function ConsortiumsPage() {
  var auth = useAuth();
  var isAdmin = auth.user?.role === "admin";
  var itemsState = useState([]);
  var totalState = useState(0);
  var countsState = useState({});
  var loadingState = useState(true);
  var searchState = useState("");
  var filterState = useState("");
  var pageState = useState(1);
  var showFormState = useState(false);
  var editingState = useState(null);
  var deletingState = useState(null);
  var clientsState = useState([]);
  var viewState = useState("list");

  var fetchData = useCallback(async function() {
    loadingState[1](true);
    try {
      var params = new URLSearchParams();
      if (searchState[0]) params.append("search", searchState[0]);
      if (filterState[0]) params.append("status", filterState[0]);
      params.append("page", pageState[0]);
      params.append("limit", "20");
      var res = await api.get("/consortiums?" + params.toString());
      itemsState[1](res.data.consortiums || []);
      totalState[1](res.data.total || 0);
      countsState[1](res.data.status_counts || {});
    } catch(err) { console.error(err); }
    finally { loadingState[1](false); }
  }, [searchState[0], filterState[0], pageState[0]]);

  useEffect(function() { fetchData(); }, [fetchData]);

  useEffect(function() {
    api.get("/clients?limit=200").then(function(r) { clientsState[1](r.data.clients || []); }).catch(function(){});
  }, []);

  async function handleDelete(id) {
    try { await api.delete("/consortiums/" + id); deletingState[1](null); fetchData(); }
    catch(err) { console.error(err); }
  }

  function handleSave() { showFormState[1](false); editingState[1](null); fetchData(); }
  function onEdit(it) { editingState[1](it); showFormState[1](true); }
  function onDel(it) { deletingState[1](it); }

  var totalPages = Math.ceil(totalState[0] / 20);

  return (
    <AppShell activePage="consorcios" title="Consorcios">
      <div data-testid="consortiums-page">
        {showFormState[0] && <ConsortiumFormModal item={editingState[0]} clients={clientsState[0]} onClose={function() { showFormState[1](false); editingState[1](null); }} onSave={handleSave} />}
        {deletingState[0] && <DeleteConsortiumModal item={deletingState[0]} onClose={function() { deletingState[1](null); }} onConfirm={handleDelete} />}

        <div className="flex items-center gap-2 mb-4">
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button onClick={function() { viewState[1]("list"); }} data-testid="view-list-button"
              className={"px-4 py-2 text-xs font-medium rounded-lg transition-all " + (viewState[0] === "list" ? "bg-white text-[#0A2F3D] shadow-sm" : "text-gray-500 hover:text-gray-700")}>
              Lista
            </button>
            <button onClick={function() { viewState[1]("map"); }} data-testid="view-map-button"
              className={"px-4 py-2 text-xs font-medium rounded-lg flex items-center gap-1.5 transition-all " + (viewState[0] === "map" ? "bg-white text-[#0A2F3D] shadow-sm" : "text-gray-500 hover:text-gray-700")}>
              <Map className="w-3.5 h-3.5" />Mapa de Cotas
            </button>
          </div>
        </div>

        {viewState[0] === "map" ? <QuotaMap /> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
          {["ativo", "contemplado", "em_atraso", "cancelado"].map(function(s) {
            var active = filterState[0] === s;
            return (
              <button key={s} onClick={function() { filterState[1](active ? "" : s); pageState[1](1); }} data-testid={"consortium-filter-" + s}
                className={"bg-white rounded-xl p-4 shadow-sm border-2 text-left " + (active ? "border-[#3B82F6] shadow-md" : "border-transparent hover:border-gray-200")}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={"w-2.5 h-2.5 rounded-full " + (STATUS_DOT[s] || "bg-gray-400")} />
                  <span className="text-xs font-medium text-gray-500">{STATUS_LABELS[s]}</span>
                </div>
                <p className="text-2xl font-bold text-[#0A2F3D]">{countsState[0][s] || 0}</p>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={searchState[0]} onChange={function(e) { searchState[1](e.target.value); pageState[1](1); }}
                placeholder="Buscar por cliente, grupo, cota ou administradora..." data-testid="consortiums-search-input"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]" />
            </div>
            <button onClick={function() { editingState[1](null); showFormState[1](true); }} data-testid="consortiums-new-button"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] whitespace-nowrap">
              <Plus className="w-4 h-4" />Novo Consorcio
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
            <span data-testid="consortiums-total-count">{totalState[0]} consorcio{totalState[0] !== 1 ? "s" : ""}</span>
            {(searchState[0] || filterState[0]) && (
              <button onClick={function() { searchState[1](""); filterState[1](""); pageState[1](1); }} className="text-[#3B82F6] hover:underline" data-testid="consortiums-clear-filters">Limpar filtros</button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden" data-testid="consortiums-table-container">
          {loadingState[0] ? (
            <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
          ) : itemsState[0].length === 0 ? (
            <div className="text-center py-16">
              <Car className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Nenhum consorcio encontrado</p>
              <p className="text-gray-400 text-sm mt-1">Cadastre ou importe consorcios</p>
            </div>
          ) : (
            <>
              <ConsortiumsTable items={itemsState[0]} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDel} />
              <ConsortiumsMobile items={itemsState[0]} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDel} />
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Pagina {pageState[0]} de {totalPages}</p>
                  <div className="flex gap-1">
                    <button disabled={pageState[0] <= 1} onClick={function() { pageState[1](pageState[0] - 1); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30">Anterior</button>
                    <button disabled={pageState[0] >= totalPages} onClick={function() { pageState[1](pageState[0] + 1); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30">Proxima</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>)}
      </div>
    </AppShell>
  );
}
